import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Activity, RefreshCw, Volume2, Clock, Users, ClipboardCopy, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

/* ---------- Traffic-light tile ----------
 * Single source of truth for status colour + label.
 * `pass` is the pre-baked boolean from the backend:
 *   true  -> 🟢 PASS
 *   false -> 🔴 NEEDS REVIEW
 *   null  -> ⚪ NO DATA YET
 */
function statusFor(pass) {
  if (pass === true)  return { dot: "🟢", label: "PASS",          color: "var(--brand-green, #3FA34D)", bg: "#DCF2E0" };
  if (pass === false) return { dot: "🔴", label: "NEEDS REVIEW",  color: "var(--coral-deep, #C92A36)", bg: "#FFE3E0" };
  return                       { dot: "⚪", label: "NO DATA YET",  color: "var(--ink-soft, #555)",      bg: "var(--paper-muted, #F2EFE7)" };
}

function PilotTile({ icon: Icon, title, pass, primary, secondary, helper, testId, informational = false }) {
  const s = statusFor(informational ? null : pass);
  return (
    <section
      className="brutal-card bg-white p-5"
      data-testid={testId}
      style={{ borderLeft: `8px solid ${informational ? "var(--brand-blue, #2563EB)" : s.color}` }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-[color:var(--ink-soft)]" />
          <div className="display font-extrabold text-base">{title}</div>
        </div>
        {!informational && (
          <div
            className="text-xs font-extrabold uppercase tracking-wider px-2 py-1 rounded"
            style={{ background: s.bg, color: s.color }}
            data-testid={`${testId}-status`}
          >
            <span className="mr-1">{s.dot}</span>{s.label}
          </div>
        )}
        {informational && (
          <div
            className="text-xs font-extrabold uppercase tracking-wider px-2 py-1 rounded"
            style={{ background: "#E0EAFD", color: "var(--brand-blue, #2563EB)" }}
          >
            INFO
          </div>
        )}
      </div>
      <div className="display text-3xl font-extrabold tabular-nums mb-1" data-testid={`${testId}-primary`}>
        {primary}
      </div>
      {secondary && (
        <div className="text-sm text-[color:var(--ink-soft)] mb-2" data-testid={`${testId}-secondary`}>
          {secondary}
        </div>
      )}
      {helper && (
        <div className="text-xs text-[color:var(--ink-soft)] leading-snug">{helper}</div>
      )}
    </section>
  );
}

export default function AdminPilot() {
  useSEO({ title: "Admin · Pilot review", noindex: true });
  const { profileId: paramId } = useParams();
  const navigate = useNavigate();
  const [profileId, setProfileId] = useState(paramId || "");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = async (id) => {
    const targetId = id || profileId;
    if (!targetId) {
      setData(null);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const r = await api.get(`/profiles/${targetId}/pilot-attempts?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paramId) {
      setProfileId(paramId);
      reload(paramId);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [paramId, days]);

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = profileId.trim();
    if (!trimmed) return;
    navigate(`/admin/pilot/${trimmed}`);
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-5 sm:px-10 py-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/region" className="pill-btn mb-4 inline-flex" data-testid="back-to-admin">
          <ArrowLeft size={14} /> Back
        </Link>
        <AdminTabs current="pilot" />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h1 className="display text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
            <Activity size={32} className="text-[color:var(--coral)]" />
            Pilot review
          </h1>
          <div className="flex gap-2 items-center">
            <select
              className="brutal-input w-auto"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="pilot-days-picker"
            >
              <option value={1}>Last 1 day</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              type="button"
              onClick={() => reload()}
              className="pill-btn"
              disabled={!profileId || loading}
              data-testid="pilot-reload"
              aria-label="Reload"
            >
              <RefreshCw size={12} />
            </button>
          </div>
        </div>

        <p className="text-[color:var(--ink-soft)] text-sm mb-5 max-w-3xl">
          A non-technical snapshot for a pilot reviewer. Each card shows pass /
          needs-review / no-data-yet against the locked criteria in
          PILOT_1_CRITERIA.md. Numbers come straight from the backend — this
          page does not re-interpret them.
        </p>

        <form
          onSubmit={onSubmit}
          className="brutal-card bg-white p-4 mb-6 flex flex-wrap items-end gap-3"
          data-testid="pilot-profile-form"
        >
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs uppercase tracking-wider font-bold mb-1">Profile ID</label>
            <input
              type="text"
              className="brutal-input w-full"
              placeholder="e.g. 7e1a3c4b-..."
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              data-testid="pilot-profile-input"
            />
          </div>
          <button type="submit" className="pill-btn primary" data-testid="pilot-load-btn">
            Load
          </button>
        </form>

        {error && (
          <div
            className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4"
            data-testid="pilot-error"
          >
            {error}
          </div>
        )}

        {loading && <p data-testid="pilot-loading">Loading…</p>}

        {!loading && !data && !error && (
          <div className="brutal-card bg-[color:var(--paper-muted)] p-4 text-sm text-[color:var(--ink-soft)]">
            Enter a profile ID above to load the pilot snapshot. The profile
            must be owned by (or shared with) the currently signed-in
            caregiver account.
          </div>
        )}

        {!loading && data && (
          <PilotResults data={data} />
        )}
      </div>
    </div>
  );
}

function PilotResults({ data }) {
  const [copied, setCopied] = useState(false);

  // Headline kid attempts: primary number, secondary "vs prior window"
  const kidTotal = data.kid_taps_total || 0;
  const priorTotal = data.prior_window_kid_taps_total || 0;
  const growthSecondary =
    data.pass_attempts_growth === null
      ? "Not enough prior-window data yet"
      : `Prior ${data.window_days}-day window: ${priorTotal} taps`;

  // TTS reliability
  const ttsPct = data.tts_reliability_pct;
  const ttsPrimary = ttsPct === null ? "—" : `${ttsPct}%`;
  const ttsSecondary =
    ttsPct === null
      ? "No TTS resolutions logged yet"
      : `${data.tts_played_to_completion} of ${data.tts_known_denominator} kid taps spoke successfully`;

  // School-time share
  const schoolPct = data.school_mode_share_pct;
  const schoolPrimary = schoolPct === null ? "—" : `${schoolPct}%`;
  const schoolSecondary =
    schoolPct === null
      ? "No kid taps with a known mode yet"
      : `${data.by_mode?.school || 0} of ${kidTotal - (data.by_mode?.unknown || 0)} known-mode kid taps were in School mode`;

  // Caregiver activity (informational only — no pass/fail per locked spec)
  const modeling = data.caregiver_modeling_total || 0;
  const editing = data.caregiver_editing_total || 0;
  const legacy = data.caregiver_legacy_total || 0;
  const caregiverPrimary = `${modeling} modeling · ${editing} editing`;
  const caregiverSecondary =
    legacy > 0
      ? `Plus ${legacy} legacy caregiver taps (pre-split). All caregiver taps are excluded from kid-attempt totals.`
      : "Caregiver taps are excluded from kid-attempt totals.";

  // ---- Clipboard builder (4 lines, ASCII status glyphs, profile prefix + full UUID) ----
  function asciiStatus(pass) {
    if (pass === true)  return "[PASS]";
    if (pass === false) return "[NEEDS REVIEW]";
    return "[NO DATA]";
  }
  function buildClipboardText() {
    const gen = new Date(data.generated_at);
    const iso = gen.toISOString().replace("T", " ").slice(0, 16); // "YYYY-MM-DD HH:MM"
    const profilePrefix = (data.profile_id || "").slice(0, 8);
    const kidLine = `Kid attempts: ${kidTotal} ${asciiStatus(data.pass_attempts_growth)} vs prior`;
    const ttsLine = `Voice playback: ${ttsPct === null ? "n/a" : ttsPct + "%"} ${asciiStatus(data.pass_tts_reliability)}`;
    const schoolLine = `School-time: ${schoolPct === null ? "n/a" : schoolPct + "%"} ${asciiStatus(data.pass_school_mode_share)}`;
    return [
      `SonSpeak Pilot · ${iso} UTC`,
      `Profile ${profilePrefix}… (full: ${data.profile_id}) · last ${data.window_days} day(s)`,
      `${kidLine} · ${ttsLine} · ${schoolLine}`,
      `Caregiver: ${modeling} modeling · ${editing} editing (info only)`,
    ].join("\n");
  }
  async function copySummary() {
    const text = buildClipboardText();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Clipboard write blocked (e.g. iframe permissions). Surface text so the
      // reviewer can still copy it manually rather than failing silently.
      window.prompt("Copy this summary:", text);
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PilotTile
          icon={Activity}
          title="Kid attempts"
          pass={data.pass_attempts_growth}
          primary={kidTotal}
          secondary={growthSecondary}
          helper="PASS = any increase in kid taps vs the prior window of the same length."
          testId="pilot-tile-attempts"
        />
        <PilotTile
          icon={Volume2}
          title="Voice playback success"
          pass={data.pass_tts_reliability}
          primary={ttsPrimary}
          secondary={ttsSecondary}
          helper="PASS = ≥ 95%. Backgrounded or timed-out taps are excluded from the denominator."
          testId="pilot-tile-tts"
        />
        <PilotTile
          icon={Clock}
          title="School-time share"
          pass={data.pass_school_mode_share}
          primary={schoolPrimary}
          secondary={schoolSecondary}
          helper="PASS = ≥ 80% of kid taps with a known mode happen in School mode."
          testId="pilot-tile-school"
        />
        <PilotTile
          icon={Users}
          title="Caregiver activity"
          pass={null}
          informational
          primary={caregiverPrimary}
          secondary={caregiverSecondary}
          helper="Modeling = caregiver intentionally demoing on the board. Editing = preview taps while the tile editor was open."
          testId="pilot-tile-caregiver"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={copySummary}
          className={`pill-btn ${copied ? "gold" : ""}`}
          data-testid="pilot-copy-summary"
          aria-live="polite"
        >
          {copied ? <Check size={14} /> : <ClipboardCopy size={14} />}
          {copied ? "Copied to clipboard" : "Copy summary for daily report"}
        </button>
        <span className="text-xs text-[color:var(--ink-soft)]">
          4-line ASCII summary · safe to paste into Google Docs, email, or Word.
        </span>
      </div>

      <div className="brutal-card bg-white p-4 mt-4 text-xs text-[color:var(--ink-soft)]" data-testid="pilot-meta">
        Profile <span className="font-mono">{data.profile_id}</span> · window {data.window_days} day(s) · generated {new Date(data.generated_at).toLocaleString()}
      </div>
    </>
  );
}
